import React, { FC, useEffect, useState } from 'react';
import translate from '@translate';
import { useDispatch } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TableSimple from 'src/components/TableSimple';
import Select from 'src/components/Select';
import { CustomRenderType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import api from '@api';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { TransportPackageType } from 'src/types/transportPackageType';
import TopBlock from './TopBlock';
import { allColumns } from './constants';

type PalletTypeRow = {
    ID: number;
    NAME: string;
    PACKAGE_TYPE_SSRC?: string | null;
};

const PalletTypesListContainer: FC = () => {
    const dispatch = useDispatch();
    const permissionUpdate = useCheckPermission('TRANSPORT_PACKAGE_TYPE.UPDATE');

    const [rows, setRows] = useState<PalletTypeRow[]>([]);
    const [packageTypes, setPackageTypes] = useState<TransportPackageType[]>([]);
    const [loading, setLoading] = useState(false);

    const loadList = async () => {
        setLoading(true);
        try {
            const [palletRes, packageRes] = await Promise.all([
                api('get', 'api/secure/order-pallets/pallet-types'),
                api('get', 'api/secure/transports/package-types?active=0'),
            ]);
            const palletData: PalletTypeRow[] = Array.isArray(palletRes.data?.data)
                ? palletRes.data.data
                : Array.isArray(palletRes.data)
                  ? palletRes.data
                  : [];
            const packageData: TransportPackageType[] = Array.isArray(packageRes.data?.data)
                ? packageRes.data.data
                : Array.isArray(packageRes.data)
                  ? packageRes.data
                  : [];
            setRows(palletData);
            setPackageTypes(packageData);
        } catch {
            setRows([]);
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('palletTypes.message.loadError'),
                })
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('palletTypes.title') }));
        void loadList();
    }, []);

    const packageTypeOptions = [
        { value: '', label: '—' },
        ...packageTypes.map((row) => ({
            value: row.SSRC_CODE,
            label: `${row.SSRC_CODE} — ${row.NAME}`,
        })),
    ];

    const saveMapping = async (id: number, packageTypeSsrc: string | null) => {
        try {
            const res = await api('put', `api/secure/order-pallets/pallet-types/${id}`, {
                PACKAGE_TYPE_SSRC: packageTypeSsrc,
            });
            const updated: PalletTypeRow | undefined = res.data?.data;
            if (updated) {
                setRows((current) => current.map((row) => (row.ID === id ? { ...row, ...updated } : row)));
            }
        } catch {
            dispatch(
                addMessage({
                    type: 'error',
                    title: translate('palletTypes.message.saveError'),
                })
            );
        }
    };

    const customRender: CustomRenderType = (column, row) => {
        if (column.key !== 'PACKAGE_TYPE_SSRC') {
            return null;
        }
        return (
            <Select
                options={packageTypeOptions}
                value={row.PACKAGE_TYPE_SSRC ?? ''}
                onChange={(value) =>
                    void saveMapping(Number(row.ID), value ? String(value) : null)
                }
                name={`pallet-type-${row.ID}-package-type`}
                fullWidth
                disabled={!permissionUpdate}
            />
        );
    };

    return (
        <div data-testid="pallet-types-list">
            <TopBlock reloadList={() => void loadList()} />
            <TableSimple
                rowIdKey="ID"
                rows={rows}
                loading={loading}
                columsLabelPrefix="palletTypes.table"
                allColumns={allColumns}
                initSort={{ column: 'NAME', direction: 'ASC' }}
                fullHeight
                heightVariant="top-tab-2"
                customRender={customRender}
            />
        </div>
    );
};

export default PalletTypesListContainer;
